import { Variants } from "framer-motion";

// ─── Page / Section Transitions ────────────────────────────────────────────
export const pageVariants: Variants = {
  hidden: { opacity: 0, y: 14 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] },
  },
  exit: { opacity: 0, y: -8, transition: { duration: 0.2 } },
};

// ─── Stagger Containers ─────────────────────────────────────────────────────
export const containerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.07, delayChildren: 0.08 } },
};

export const fastContainerVariants: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.04, delayChildren: 0.02 } },
};

// ─── Stagger Items ──────────────────────────────────────────────────────────
export const itemVariants: Variants = {
  hidden: { opacity: 0, y: 22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.38, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

export const fastItemVariants: Variants = {
  hidden: { opacity: 0, x: -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.24, ease: "easeOut" },
  },
};

// ─── Fade ───────────────────────────────────────────────────────────────────
export const fadeInVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
};

// ─── Modal ──────────────────────────────────────────────────────────────────
export const backdropVariants: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.2 } },
  exit: { opacity: 0, transition: { duration: 0.22, delay: 0.05 } },
};

export const modalVariants: Variants = {
  hidden: { opacity: 0, y: 36, scale: 0.96 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 380, damping: 30 },
  },
  exit: { opacity: 0, y: 18, scale: 0.97, transition: { duration: 0.18 } },
};

// ─── Slide ──────────────────────────────────────────────────────────────────
export const slideDownVariants: Variants = {
  hidden: { opacity: 0, y: -22 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] },
  },
};

export const slideUpVariants: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 420, damping: 30 },
  },
  exit: { opacity: 0, y: 10, transition: { duration: 0.18 } },
};

// ─── Scale pop (EmptyState icon, badges) ────────────────────────────────────
export const scalePopVariants: Variants = {
  hidden: { opacity: 0, scale: 0.7 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { type: "spring", stiffness: 500, damping: 22 },
  },
};

// ─── Clay-specific variants ─────────────────────────────────────────────────

/** Button / small element press — squish effect */
export const clayPressVariants: Variants = {
  idle: { scale: 1 },
  pressed: {
    scale: 0.97,
    transition: { duration: 0.08, ease: "easeIn" },
  },
};

/** Card / panel hover — lift with spring overshoot */
export const clayHoverLift = {
  y: -4,
  transition: {
    duration: 0.2,
    ease: [0.34, 1.56, 0.64, 1], // spring overshoot
  },
};

/** Modal open/close with clay spring */
export const clayModalVariants: Variants = {
  hidden: { opacity: 0, scale: 0.92 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.22,
      ease: [0.34, 1.56, 0.64, 1],
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.16, ease: "easeIn" },
  },
};

/** Banner slide-in from top */
export const clayBannerVariants: Variants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: [0.34, 1.56, 0.64, 1],
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: { duration: 0.2, ease: "easeIn" },
  },
};

/** MachhaCharacter entrance bounce */
export const clayCharacterVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 15 },
  },
};

/** Toast pop-in */
export const clayToastVariants: Variants = {
  hidden: { opacity: 0, scale: 0.8, y: -10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: "spring", stiffness: 500, damping: 25 },
  },
  exit: { opacity: 0, scale: 0.9, transition: { duration: 0.15 } },
};

/** Error wobble / shake — use with animate prop directly */
export const clayErrorShake = {
  rotate: [0, -3, 3, -2, 2, 0],
  transition: { duration: 0.4 },
};

/** Stagger container for cartoony cascade entrances */
export const clayStaggerContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05, delayChildren: 0.05 } },
};

/** Stagger child — pop in with spring */
export const clayStaggerItem: Variants = {
  hidden: { opacity: 0, y: 16, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 400, damping: 25 },
  },
};

// ─── Micro-interaction helpers (use inline) ─────────────────────────────────
export const tapScale = { scale: 0.95 };
export const clayTapScale = { scale: 0.97 };
export const hoverLift = {
  y: -4,
  transition: { type: "spring" as const, stiffness: 400, damping: 25 },
};
export const clayHoverBounce = {
  y: -4,
  transition: { type: "spring" as const, stiffness: 400, damping: 20 },
};
export const hoverScale = {
  scale: 1.03,
  transition: { type: "spring" as const, stiffness: 400, damping: 25 },
};
